import { createContext, Fragment, type ReactNode } from 'react';

import { type ProjectedTranscriptRow } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { continuationOf } from '@apps/web/features/phoebe_chat_v3/ui/message_continuation_context';
import { AttachmentGroup } from '@apps/web/features/phoebe_chat_v3/ui/primitives/attachment_cards';

export const MessagePresentationsContext = createContext<ReactNode>(null);

// Compose after activity projection: hidden tool rows must not split a reply.
// Presentations belong to the text, so exclude them when asking the same
// continuation function that determines message adjacency. The final text
// row receives the attachment group before its shared response actions.
export function composeMessageAttachments(
    occurrences: ReadonlyArray<ProjectedTranscriptRow>,
    attachmentOwnerIds: ReadonlySet<string>,
    presentationOwnerIds: ReadonlySet<string>,
): ReadonlyArray<ProjectedTranscriptRow> {
    if (attachmentOwnerIds.size === 0) {
        return occurrences;
    }
    function isPresentation(
        row: ProjectedTranscriptRow,
    ): row is Extract<ProjectedTranscriptRow, { kind: 'occurrence' }> {
        return (
            row.kind === 'occurrence' &&
            presentationOwnerIds.has(row.occurrenceId)
        );
    }
    // Day labels describe displayed rows, not response boundaries. A tool
    // from before midnight can move behind an answer written after midnight.
    const messageRows = occurrences.filter(
        (row) => row.kind !== 'day_heading' && !isPresentation(row),
    );
    const messageIndices = new Map(
        messageRows.map((row, index) => [row.rowId, index]),
    );
    const occurrenceIndices = new Map(
        occurrences.map((row, index) => [row.rowId, index]),
    );
    const rows: Array<ProjectedTranscriptRow> = [];
    for (let index = 0; index < occurrences.length;) {
        const occurrence = occurrences[index]!;
        let end = index + 1;
        let lastTextRowId: string | null = null;
        if (
            occurrence.kind === 'occurrence' &&
            occurrence.messageSender === 'assistant'
        ) {
            let messageIndex = messageIndices.get(occurrence.rowId)!;
            while (continuationOf(messageRows, messageIndex).continuedByNext) {
                messageIndex += 1;
            }
            lastTextRowId = messageRows[messageIndex]!.rowId;
            const nextMessage = messageRows[messageIndex + 1];
            end =
                nextMessage === undefined
                    ? occurrences.length
                    : occurrenceIndices.get(nextMessage.rowId)!;
        } else if (isPresentation(occurrence)) {
            while (
                end < occurrences.length &&
                (isPresentation(occurrences[end]!) ||
                    occurrences[end]!.kind === 'day_heading')
            ) {
                end += 1;
            }
        }
        const response = occurrences.slice(index, end);
        const attachments = response
            .filter(isPresentation)
            .filter((row) => attachmentOwnerIds.has(row.occurrenceId));
        if (attachments.length === 0) {
            rows.push(...response);
            index = end;
            continue;
        }
        const attachmentNodes = attachments.map((row) => (
            <Fragment key={row.occurrenceId}>{row.node}</Fragment>
        ));
        const group =
            attachments.length > 1 ? (
                <AttachmentGroup>{attachmentNodes}</AttachmentGroup>
            ) : (
                attachmentNodes
            );
        const attachmentEventIds = attachments.flatMap((row) => row.eventIds);
        for (
            let responseIndex = 0;
            responseIndex < response.length;
            responseIndex += 1
        ) {
            const row = response[responseIndex]!;
            if (
                row.kind === 'occurrence' &&
                attachmentOwnerIds.has(row.occurrenceId)
            ) {
                // Without text, retain the last file's standalone column and
                // leave intervening tables and widgets at their own positions.
                if (
                    lastTextRowId === null &&
                    row === attachments[attachments.length - 1]
                ) {
                    rows.push({
                        ...row,
                        eventIds: attachmentEventIds,
                        node: (
                            <div className="w-full max-w-[65ch] min-w-0">
                                {group}
                            </div>
                        ),
                    });
                }
                continue;
            }
            if (
                row.kind !== 'occurrence' ||
                row.messageSender !== 'assistant'
            ) {
                rows.push(row);
                continue;
            }
            const presentations: Array<ReactNode> = [];
            const eventIds = [...row.eventIds];
            for (
                let nextIndex = responseIndex + 1;
                nextIndex < response.length;
                nextIndex += 1
            ) {
                const next = response[nextIndex]!;
                if (next.kind === 'day_heading') {
                    continue;
                }
                if (!isPresentation(next)) {
                    break;
                }
                if (!attachmentOwnerIds.has(next.occurrenceId)) {
                    presentations.push(
                        <Fragment key={next.occurrenceId}>
                            {next.node}
                        </Fragment>,
                    );
                    eventIds.push(...next.eventIds);
                }
                // Consume headings only when a presentation follows them.
                // A trailing heading still belongs to the next message.
                responseIndex = nextIndex;
            }
            if (row.rowId === lastTextRowId) {
                presentations.push(
                    <Fragment key="attachments">{group}</Fragment>,
                );
                eventIds.push(...attachmentEventIds);
            }
            rows.push({
                ...row,
                eventIds,
                node: (
                    <MessagePresentationsContext.Provider
                        value={presentations.length > 0 ? presentations : null}
                    >
                        {row.node}
                    </MessagePresentationsContext.Provider>
                ),
            });
        }
        index = end;
    }
    // Moving files can empty a day section. Keep the label of the next
    // surviving row, and suppress repeated dates and single-day headings.
    const datedRows: Array<ProjectedTranscriptRow> = [];
    let pendingHeading: Extract<
        ProjectedTranscriptRow,
        { kind: 'day_heading' }
    > | null = null;
    let previousDay: string | null = null;
    const days = new Set<string>();
    for (const row of rows) {
        if (row.kind === 'day_heading') {
            pendingHeading = row;
            continue;
        }
        if (pendingHeading !== null) {
            if (pendingHeading.day !== previousDay) {
                datedRows.push(pendingHeading);
                days.add(pendingHeading.day);
                previousDay = pendingHeading.day;
            }
            pendingHeading = null;
        }
        datedRows.push(row);
    }
    return days.size > 1
        ? datedRows
        : datedRows.filter((row) => row.kind !== 'day_heading');
}
